import ChatIcon from '@mui/icons-material/Chat';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import React, { memo, startTransition, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { SideBarLink } from '../../../pages/panalyt-superadmin/SideBar';
import { EffectiveRoleViewAuthorizationService } from '../../v2/api/authorization/service';
import {
  useListUserViewCustomDashboardConfigs,
  useListUserViewDashboards,
} from '../../v2/api/dashboards/hooks';
import { FeatureAccessService } from '../../v2/api/featureaccess/service';
import { FeatureFlagsService } from '../../v2/api/featureflags/service';
import { BetterSuspense } from '../../v2/common/components/BetterSuspense';
import {
  getCustomDashboardLink,
  getCustomDashboardPageLink,
} from '../../v2/common/components/dashboards/utils';
import { LoadingCircle } from '../../v2/common/components/LoadingCircle';
import { ROUTE_URLS } from '../../v2/constants';
import {
  useEffectiveRoleViewAuthorizationServiceContext,
  useFeatureAccessServiceContext,
  useFeatureFlagsServiceContext,
  useGlobalLocaleContext,
  useReactRouterHistoryContext,
} from '../../v2/context/contexts';
import { NavBarItemObj } from './NavBarItem';
import NavTraySection from './NavTraySection';

export const panalytSuperAdminConsoleItems: SideBarLink[] = [
  {
    id: 'companyList',
    name: 'common:navigation.companyList',
    link: '/panalyt-superadmin/company-list',
    avatar: <SupervisedUserCircleIcon color="secondary" />,
    className: 'intercom_panalyt-super-admin_company-list',
  },
  {
    id: 'companyDashboards',
    name: 'common:navigation.companyDashboards',
    link: '/panalyt-superadmin/company-dashboards',
    avatar: <SupervisedUserCircleIcon color="secondary" />,
    className: 'intercom_panalyt-super-admin_company-dashboards',
  },
  {
    id: 'domainSettings',
    name: 'common:navigation.domainPreferences',
    link: '/panalyt-superadmin/manage-company/domain-preferences',
    avatar: <SupervisedUserCircleIcon color="secondary" />,
    className: 'intercom_panalyt-super-admin_domain-settings',
  },
  {
    id: 'manageMetrics',
    name: 'common:navigation.manageMetrics',
    link: '/panalyt-superadmin/manage-metrics',
    avatar: <SupervisedUserCircleIcon color="secondary" />,
    className: 'intercom_panalyt-super-admin_manage-metrics',
  },
  {
    id: 'multiDomainAuth',
    name: 'common:navigation.multiDomainAuth',
    link: '/panalyt-superadmin/multi-domain-auth',
    avatar: <SupervisedUserCircleIcon color="secondary" />,
    className: 'intercom_panalyt-super-admin_multi-domain-auth',
  },
  {
    id: 'auditLogs',
    name: 'common:navigation.auditLogs',
    link: '/panalyt-superadmin/audit-logs',
    avatar: <SupervisedUserCircleIcon color="secondary" />,
    className: 'intercom_panalyt-super-admin_audit-logs',
  },
  {
    id: 'batchActions',
    name: 'common:navigation.batchActions',
    link: '/panalyt-superadmin/batch-actions',
    avatar: <SupervisedUserCircleIcon color="secondary" />,
    className: 'intercom_panalyt-super-admin_batch-actions',
  },
  {
    id: 'chappiConversations',
    name: 'common:navigation.chappiConversations',
    link: '/panalyt-superadmin/chappi-conversations',
    avatar: <ChatIcon color="secondary" />,
    className: 'intercom_panalyt-super-admin_chappi-conversations',
  },
  {
    id: 'backendApi',
    name: 'common:navigation.backendApi',
    link: '/panalyt-superadmin/backend-api',
    avatar: <SupervisedUserCircleIcon color="secondary" />,
    className: 'intercom_panalyt-super-admin_backend-api',
  },
  {
    id: 'manageDomainSettings',
    name: 'common:navigation.manageDomainSettings',
    link: '/panalyt-superadmin/manage-domain-settings',
    avatar: <SupervisedUserCircleIcon color="secondary" />,
    className: 'intercom_panalyt-super-admin_manage-domain-settings',
  },
  {
    id: 'manageDomainPreferences',
    name: 'common:navigation.manageDomainPreferences',
    link: '/panalyt-superadmin/manage-domain-preferences',
    avatar: <SupervisedUserCircleIcon color="secondary" />,
    className: 'intercom_panalyt-super-admin_manage-domain-preferences',
  },
];

const MenuItemButton = styled.div`
  text-decoration: none;
  color: unset;
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const myPanalytLink = '/my-panalyt';

const NavTrayContainer = styled.div`
  // overflow: auto;
`;
// TODO: disabling overflow here as it is somehow causing menu in NavTrayItem to not appear(can check with Dashboard list on nav tray).
//  Ideally there should be overflow here as well, so investigate later and fix.

interface NavigationTrayProps {
  toggleMenu: <T>(e: React.MouseEvent<T>) => void;
  closeMenu: <T>(e: React.MouseEvent<T>) => void;
}

interface AdminConsoleItem {
  id: string;
  name: string;
  link: string;
  className: string;
  isEnabled?: () => boolean;
}

const adminConsoleItems = (
  authorizationService: EffectiveRoleViewAuthorizationService,
  featureAccessService: FeatureAccessService,
  featureFlagsService: FeatureFlagsService
) => {
  const userPermissions: AdminConsoleItem = {
    id: 'permissions',
    name: 'common:navigation.permissions',
    link: ROUTE_URLS.PERMISSIONS.getFullPath(),
    className: 'intercom_nt_permissions',
    isEnabled: () => featureAccessService.canViewUserPermissions(),
  };
  const alias: AdminConsoleItem = {
    id: 'alias',
    name: 'common:navigation.alias',
    link: ROUTE_URLS.ALIAS.getFullPath(),
    className: 'intercom_nt_alias',
    isEnabled: () => featureFlagsService.isAliasEnabled(),
  };
  const dataUpload: AdminConsoleItem = {
    id: 'dataUpload',
    name: 'common:navigation.dataUpload',
    link: ROUTE_URLS.DATA_UPLOAD.getFullPath(),
    className: 'intercom_nt_dataUpload',
    isEnabled: () => authorizationService.canUploadClientRawData(),
  };

  return [userPermissions, alias, dataUpload].filter((item) =>
    item.isEnabled ? item.isEnabled() : true
  );
};

const CustomDashboardsNavTraySection = ({
  toggleMenu,
  closeMenu,
}: {
  toggleMenu: <T>(e: React.MouseEvent<T>) => void;
  closeMenu: <T>(e: React.MouseEvent<T>) => void;
}) => {
  const allowedCustomDashboards = useListUserViewCustomDashboardConfigs();
  const locale = useGlobalLocaleContext();
  const customDashboardSubItems: NavBarItemObj[] = allowedCustomDashboards.map(
    (db) => {
      return {
        id: db.id,
        name: db.title?.[locale.selected],
        link: getCustomDashboardLink(db.id),
        subItems: db.pages
          .filter((p) => p.enabled)
          .map((p) => {
            return {
              id: p.pageId,
              name: p.title?.[locale.selected],
              link: getCustomDashboardPageLink(db.id, p.pageId),
            };
          }),
      };
    }
  );

  return customDashboardSubItems.length > 0 ? (
    <NavTraySection
      toggleMenu={toggleMenu}
      closeMenu={closeMenu}
      name={'common:navigation.customDashboards'}
      subItems={customDashboardSubItems}
      color="#25b1e4"
      openByDefault={false}
      className="intercom_nt_db"
    />
  ) : null;
};

const DashboardNavTraySection = ({
  toggleMenu,
  closeMenu,
}: {
  toggleMenu: <T>(e: React.MouseEvent<T>) => void;
  closeMenu: <T>(e: React.MouseEvent<T>) => void;
}) => {
  const { dashboards: allowedDashboards, isLoading } =
    useListUserViewDashboards();
  if (isLoading) {
    return <LoadingCircle color="secondary" />;
  }

  const dashboardSubItems: NavBarItemObj[] = allowedDashboards.map((db) => ({
    id: db.id,
    name: db.nameKey,
    link: db.link,
    subItems: db.pages.map((p) => ({ ...p, name: p.nameKey })),
    disabled: !db.enabled,
  }));

  return dashboardSubItems.length > 0 ? (
    <NavTraySection
      toggleMenu={toggleMenu}
      closeMenu={closeMenu}
      name={'common:navigation.dashboards'}
      subItems={dashboardSubItems}
      color="#FCA720"
      openByDefault={true}
      className="intercom_nt_db"
    />
  ) : null;
};

const StyledAccordion = styled(Accordion)`
  background-color: transparent;
  box-shadow: none;
  &:before {
    display: none;
  }
  margin: 0;
  /* Remove extra spacing when expanded */
  &.Mui-expanded {
    margin: 0;
  }
`;

const StyledAccordionSummary = styled(AccordionSummary)`
  padding: 0 16px;
  min-height: 48px;
  background-color: #93234e;
  color: #ffffff;

  .MuiAccordionSummary-content {
    margin: 12px 0;
  }

  /* Remove extra margin when expanded */
  &.Mui-expanded {
    min-height: 48px;
    margin: 0;
  }
`;

const StyledAccordionDetails = styled(AccordionDetails)`
  padding: 0;
  max-height: 25vh;
  overflow: auto;
  background-color: #6d1939;
  color: #ffffff;
`;

const StyledListItem = styled.div`
  padding: 8px 15px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #47323a;
  }
`;

const NavigationTray = memo((props: NavigationTrayProps) => {
  const { toggleMenu, closeMenu } = props;
  const history = useReactRouterHistoryContext();
  const featureAccessService = useFeatureAccessServiceContext();
  const featureFlagsService = useFeatureFlagsServiceContext();
  const authorizationService =
    useEffectiveRoleViewAuthorizationServiceContext();

  // Memoize the admin console items to prevent recalculation on every render
  const adminItems = useMemo(
    () =>
      adminConsoleItems(
        authorizationService,
        featureAccessService,
        featureFlagsService
      ),
    [authorizationService, featureAccessService, featureFlagsService]
  );

  // Handle navigation with delay
  const handleNavigation = useCallback(
    (link: string, e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Force close any open popper menus by clicking the body
      document.body.click();

      // Use startTransition to batch state updates
      startTransition(() => {
        // Close the menu
        closeMenu(e);

        // Then navigate immediately after
        history.push(link);
      });
    },
    [closeMenu, history]
  );

  const showCustomDashboards = featureFlagsService.isCustomDashboardsEnabled();

  return (
    <>
      <NavTrayContainer>
        <div onClick={(e) => handleNavigation(myPanalytLink, e)}>
          <NavTraySection
            toggleMenu={toggleMenu}
            closeMenu={closeMenu}
            name="common:navigation.myPanalyt"
            color="rgb(38,166,154)"
            className="intercom_nt_mypanalyt"
          />
        </div>
        {showCustomDashboards && (
          <BetterSuspense fallback={<LoadingCircle color="secondary" />}>
            <CustomDashboardsNavTraySection
              toggleMenu={toggleMenu}
              closeMenu={closeMenu}
            />
          </BetterSuspense>
        )}
        <DashboardNavTraySection
          toggleMenu={toggleMenu}
          closeMenu={closeMenu}
        />
        {authorizationService.canSeeSuperAdminMenu() && (
          <StyledAccordion
            defaultExpanded={false}
            className="intercom_nt_admin"
          >
            <StyledAccordionSummary
              expandIcon={<ExpandMoreIcon style={{ color: '#ffffff' }} />}
              aria-controls="admin-accordion-content"
              id="admin-accordion-header"
            >
              <Typography>{t('common:navigation.admin')}</Typography>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              {adminItems.map(({ id, name, link }) => (
                <MenuItemButton
                  key={id}
                  onClick={(e) => handleNavigation(link, e)}
                  data-menu-item={`admin-${id}`}
                >
                  <StyledListItem>{t(name)}</StyledListItem>
                </MenuItemButton>
              ))}
            </StyledAccordionDetails>
          </StyledAccordion>
        )}
        {authorizationService.canSeePanalytSuperAdminMenu() && (
          <StyledAccordion
            defaultExpanded={false}
            className="intercom_nt_admin"
          >
            <StyledAccordionSummary
              expandIcon={<ExpandMoreIcon style={{ color: '#ffffff' }} />}
              aria-controls="superadmin-accordion-content"
              id="superadmin-accordion-header"
            >
              <Typography>{t('common:navigation.superAdmin')}</Typography>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              {panalytSuperAdminConsoleItems.map(({ id, name, link }) => (
                <MenuItemButton
                  key={id}
                  onClick={(e) => handleNavigation(link, e)}
                  data-menu-item={`superadmin-${id}`}
                >
                  <StyledListItem>{t(name)}</StyledListItem>
                </MenuItemButton>
              ))}
            </StyledAccordionDetails>
          </StyledAccordion>
        )}
      </NavTrayContainer>
    </>
  );
});

// Add display name for debugging
NavigationTray.displayName = 'NavigationTray';

export default NavigationTray;
